import { useRef, useCallback, useEffect } from 'react'
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from 'react-joyride'

// Tessan brand colors
const TESSAN_GREEN = '#0F352D'
const TESSAN_YELLOW = '#FFF198'

interface OnboardingTourProps {
  isRunning: boolean
  onComplete: () => void
  onSkip: () => void
  communitySlug?: string
}

const pharmacienSteps: Step[] = [
  {
    target: '[data-tour="defis"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Bienvenue sur Tessan Community !</h3>
        <p className="text-sm text-gray-600">
          Vous voici sur la page <strong>Défis</strong>. C'est ici que vous trouverez vos objectifs
          et challenges pour gagner des points et débloquer des récompenses.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="community"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Communauté</h3>
        <p className="text-sm text-gray-600">
          Échangez avec vos confrères pharmaciens ! Posez des questions, partagez vos expériences
          et aidez les autres membres. <strong>Votre premier message vous rapportera des points !</strong>
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="toolbox"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Boîte à outils</h3>
        <p className="text-sm text-gray-600">
          Retrouvez ici toutes les ressources et outils mis à votre disposition pour
          faciliter votre quotidien en officine.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="faq"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">FAQ</h3>
        <p className="text-sm text-gray-600">
          Des questions ? Consultez notre FAQ pour trouver rapidement des réponses
          aux questions les plus fréquentes.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="account"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Mon compte</h3>
        <p className="text-sm text-gray-600">
          Suivez votre progression, vos points et vos badges dans votre espace personnel.
          C'est aussi ici que vous pouvez modifier vos informations.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
]

const medecinSteps: Step[] = [
  {
    target: '[data-tour="defis"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Bienvenue sur Tessan Community !</h3>
        <p className="text-sm text-gray-600">
          Vous voici sur la page <strong>Défis</strong>. C'est ici que vous trouverez vos objectifs
          et challenges pour gagner des points et débloquer des récompenses.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="chat"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Chat</h3>
        <p className="text-sm text-gray-600">
          Échangez avec vos confrères médecins ! Posez des questions, partagez vos expériences
          et aidez les autres membres. <strong>Votre premier message vous rapportera des points !</strong>
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="contenu"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Contenu</h3>
        <p className="text-sm text-gray-600">
          Retrouvez ici des articles, guides et ressources pour enrichir votre pratique
          médicale au quotidien.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="faq"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">FAQ</h3>
        <p className="text-sm text-gray-600">
          Des questions ? Consultez notre FAQ pour trouver rapidement des réponses
          aux questions les plus fréquentes.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="boutique"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Ma boutique</h3>
        <p className="text-sm text-gray-600">
          Utilisez vos points pour débloquer des récompenses exclusives dans la boutique Tessan.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="account"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Mon compte</h3>
        <p className="text-sm text-gray-600">
          Suivez votre progression, vos points et vos badges dans votre espace personnel.
          C'est aussi ici que vous pouvez modifier vos informations.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
]

const patientSteps: Step[] = [
  {
    target: '[data-tour="accueil"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Bienvenue sur Tessan Community !</h3>
        <p className="text-sm text-gray-600">
          Découvrez votre espace communautaire Tessan. Ici, vous pouvez échanger,
          consulter du contenu et gagner des points !
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="chat"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Chat</h3>
        <p className="text-sm text-gray-600">
          Échangez avec la communauté ! Posez des questions et partagez vos expériences.
          <strong> Votre premier message vous rapportera des points !</strong>
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="contenu"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Contenu</h3>
        <p className="text-sm text-gray-600">
          Retrouvez ici des articles et ressources utiles pour votre santé et bien-être.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="faq"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">FAQ</h3>
        <p className="text-sm text-gray-600">
          Des questions ? Consultez notre FAQ pour trouver rapidement des réponses.
          Ce lien vous redirigera vers notre centre d'aide.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="account"]',
    content: (
      <div>
        <h3 className="font-heading text-lg font-bold mb-2">Mon compte</h3>
        <p className="text-sm text-gray-600">
          Suivez votre progression, vos points et vos badges dans votre espace personnel.
          C'est aussi ici que vous pouvez modifier vos informations.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
]

const getTourSteps = (communitySlug?: string): Step[] => {
  if (communitySlug === 'pharmaciens') return pharmacienSteps
  if (communitySlug === 'patients') return patientSteps
  return medecinSteps
}

export function OnboardingTour({ isRunning, onComplete, onSkip, communitySlug }: OnboardingTourProps) {
  const steps = getTourSteps(communitySlug)

  // Refs to prevent multiple callback calls (Joyride can fire callbacks multiple times)
  const hasCompletedRef = useRef(false)
  const hasSkippedRef = useRef(false)

  // Reset refs when tour starts - but ONLY if the tour wasn't just completed
  // This allows legitimate restarts (user clicking "restart") while preventing
  // buggy restarts from race conditions
  useEffect(() => {
    if (isRunning && !hasCompletedRef.current) {
      hasSkippedRef.current = false
    }
  }, [isRunning])

  const handleCallback = useCallback((data: CallBackProps) => {
    const { status, action, type, index } = data

    // Early return if already handled to prevent any further processing
    if (hasCompletedRef.current || hasSkippedRef.current) {
      return
    }

    // Tour completed - only call once
    if (status === STATUS.FINISHED) {
      hasCompletedRef.current = true
      onComplete()
      return
    }

    // User explicitly skipped the tour (clicked "Passer le tour")
    if (status === STATUS.SKIPPED) {
      hasSkippedRef.current = true
      onSkip()
      return
    }

    // User closed the tooltip manually (X button) - treat as skip
    // But NOT when it's the last step (that should be handled by STATUS.FINISHED)
    if (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER && index < steps.length - 1) {
      hasSkippedRef.current = true
      onSkip()
      return
    }
  }, [onComplete, onSkip, steps.length])

  return (
    <Joyride
      steps={steps}
      run={isRunning}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      spotlightClicks
      disableOverlayClose
      callback={handleCallback}
      locale={{
        back: 'Précédent',
        close: 'Fermer',
        last: 'Terminer',
        next: 'Suivant',
        open: 'Ouvrir',
        skip: 'Passer le tour',
      }}
      styles={{
        options: {
          primaryColor: TESSAN_GREEN,
          backgroundColor: '#fff',
          textColor: '#1f2937',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 12,
          padding: 20,
        },
        tooltipTitle: {
          fontFamily: 'Recoleta, serif',
          fontSize: 18,
          fontWeight: 700,
        },
        tooltipContent: {
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontSize: 14,
          padding: '12px 0',
        },
        buttonNext: {
          backgroundColor: TESSAN_GREEN,
          borderRadius: 8,
          fontWeight: 600,
          fontSize: 14,
          padding: '10px 20px',
        },
        buttonBack: {
          color: TESSAN_GREEN,
          marginRight: 10,
          fontWeight: 500,
        },
        buttonSkip: {
          color: '#6b7280',
          fontSize: 13,
        },
        spotlight: {
          borderRadius: 8,
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
        beacon: {
          display: 'none',
        },
        beaconInner: {
          backgroundColor: TESSAN_YELLOW,
        },
        beaconOuter: {
          backgroundColor: `${TESSAN_YELLOW}40`,
          borderColor: TESSAN_YELLOW,
        },
      }}
      floaterProps={{
        styles: {
          arrow: {
            length: 8,
            spread: 16,
          },
        },
      }}
    />
  )
}
